import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import { CaseResponseOpenCurrencyEnum, type CaseResponse } from '../services/generated/Api';

/** True for cases sold in the general shop — active, and priced in credits (not
 *  SHOT BAND bonus coins). Bonus-currency cases only surface on `/band/shelf`;
 *  two currencies in one grid would confuse the price tags. */
export function isGeneralShopCase(c: CaseResponse): boolean {
  return c.isActive && c.openCurrency !== CaseResponseOpenCurrencyEnum.Bonus;
}

/** True for SHOT BAND's bonus-coin cases — the `/band/shelf` grid. */
export function isBandShelfCase(c: CaseResponse): boolean {
  return c.isActive && c.openCurrency === CaseResponseOpenCurrencyEnum.Bonus;
}

export function useCasesQuery() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.cases,
    queryFn: async () => {
      const cases = await apiClient.api.caseControllerGetCases();
      return (cases || []).filter(isGeneralShopCase);
    },
  });
}

export function useUniversalCasesQuery() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...queryKeys.cases, 'universal'],
    queryFn: async () => {
      const cases = await apiClient.api.caseControllerGetUniversalCases();
      return (cases || []).filter(isGeneralShopCase);
    },
  });
}

/** SHOT BAND rakeback shelf — bonus-coin cases, visible (locked) to non-members too. */
export function useBandCasesQuery() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...queryKeys.cases, 'band-shelf'],
    queryFn: async () => {
      const cases = await apiClient.api.caseControllerGetCases();
      return (cases || []).filter(isBandShelfCase);
    },
  });
}

export function useCaseItemDrops(caseId?: string, hashName?: string) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseItemDrops(caseId ?? '', hashName ?? ''),
    queryFn: () => apiClient.api.caseControllerGetCaseItemDrops({ caseId: caseId!, hashName: hashName! }),
    enabled: !!caseId && !!hashName,
    staleTime: 60 * 1000,
  });
}

export function useCaseDetails(caseId: string, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseDetails(caseId),
    queryFn: () => apiClient.api.caseControllerGetCaseDetails(caseId),
    enabled: enabled && !!caseId,
    staleTime: 5 * 60 * 1000, // 5 минут - предметы кейса не меняются часто
    gcTime: 10 * 60 * 1000,
  });
}
